// Per-project setup route. Reuses the wizard's UploadZone + ProcessingStatus
// to allow re-uploading a misformatted file or kicking off Modal again.
//
// Lives at /producer/projects/$projectId/setup. The new-project wizard
// navigates here on completion so the user can watch the pipeline finish.
//
// EpisodeSetupCard is `React.lazy`-imported so the project-setup feature
// surface (UploadZone + validation + the upload hook) ships in the
// `feature-producer` chunk and isn't pulled into the actor bundle.

import { copy } from '@ohear/shared'
import { Card, EmptyState, Spinner, Tab, TabList, TabPanel, Tabs } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { Suspense, lazy } from 'react'
import { QcThresholdsForm } from '../../../features/project-setup/QcThresholdsForm'
import { useEpisodes } from '../../../lib/api/episodes'
import { useProducerProject } from '../../../lib/api/producer-projects'
import { useRequireRole } from '../../../lib/auth'
import { useT } from '../../../lib/i18n/use-t'
import { useProducerProjectChannel } from '../../../lib/realtime-projects'

const EpisodeSetupCard = lazy(() =>
  import('../../../features/project-setup/EpisodeSetupCard').then((m) => ({
    default: m.EpisodeSetupCard,
  })),
)

export const Route = createFileRoute('/producer/projects/$projectId/setup')({
  component: ProjectSetupPage,
})

function ProjectSetupPage() {
  useRequireRole(['owner', 'member'])
  const t = useT()
  const { projectId } = Route.useParams()

  const projectQ = useProducerProject(projectId)
  const episodesQ = useEpisodes(projectId)
  useProducerProjectChannel(projectQ.data?.organization_id ?? null)

  if (projectQ.isLoading || episodesQ.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
          <Spinner /> Loading project setup…
        </div>
      </Card>
    )
  }
  if (!projectQ.data) {
    return (
      <Card>
        <p className="m-0 text-[13px] text-[var(--red)]">{t(copy.common.NOT_FOUND.project)}</p>
      </Card>
    )
  }

  const episodes = episodesQ.data ?? []

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1 pb-4 border-b border-[var(--border)]">
        <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Setup</div>
        <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0 truncate">
          {projectQ.data.name}
        </h1>
        <p className="text-[13px] text-[var(--text-2)] m-0">
          Upload reference files for each episode and watch the processing pipeline.
        </p>
      </header>

      <Tabs defaultValue="episodes">
        <TabList aria-label="Project setup sections">
          <Tab value="episodes">Episodes</Tab>
          <Tab value="qc">QC settings</Tab>
        </TabList>

        <TabPanel value="episodes">
          {episodes.length === 0 ? (
            <EmptyState
              title="No episodes yet"
              description="Add an episode to start uploading reference files."
            />
          ) : (
            <Suspense
              fallback={
                <div className="flex justify-center p-12">
                  <Spinner size="lg" label="Loading project setup…" />
                </div>
              }
            >
              <div className="space-y-6">
                {episodes.map((ep) => (
                  <EpisodeSetupCard key={ep.id} episode={ep} />
                ))}
              </div>
            </Suspense>
          )}
        </TabPanel>

        <TabPanel value="qc">
          <QcThresholdsForm projectId={projectId} initial={null} />
        </TabPanel>
      </Tabs>
    </section>
  )
}
